import * as React from "react"
import { HexColorPicker } from "react-colorful"
import * as ReactDOM from "react-dom"

type PaintbrushColorPickerProps = {
  color: string
  onChange: (color: string) => void
  size?: number
  title?: string
  placement?: "bottom" | "right" | "left"
}

const DEFAULT_SWATCHES = [
  "#111827", "#374151", "#6B7280", "#9CA3AF", "#D1D5DB", "#F3F4F6", "#FFFFFF",
  "#7F1D1D", "#B91C1C", "#EF4444", "#F87171", "#FCA5A5",
  "#7C2D12", "#C2410C", "#F97316", "#FB923C", "#FDBA74",
  "#78350F", "#CA8A04", "#EAB308", "#FACC15", "#FDE047",
  "#14532D", "#15803D", "#22C55E", "#4ADE80", "#86EFAC",
  "#134E4A", "#0F766E", "#14B8A6", "#2DD4BF", "#5EEAD4",
  "#1E3A8A", "#1D4ED8", "#3B82F6", "#60A5FA", "#93C5FD",
  "#312E81", "#4338CA", "#6366F1", "#818CF8", "#A5B4FC",
  "#581C87", "#7E22CE", "#A855F7", "#C084FC", "#D8B4FE",
  "#831843", "#BE185D", "#EC4899", "#F472B6", "#F9A8D4",
  "#A16207", "#0C4A6E"
]

function normalizeHex(value: string): string | null {
  const trimmed = value.trim()
  const normalized = trimmed.startsWith("#") ? trimmed : `#${trimmed}`
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) return null
  if (normalized.length === 4) {
    const [r, g, b] = normalized.slice(1).split("")
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
  }
  return normalized.toUpperCase()
}

function hexToRgb(hex: string): [number, number, number] {
  const normalized = normalizeHex(hex) || "#000000"
  const r = parseInt(normalized.slice(1, 3), 16)
  const g = parseInt(normalized.slice(3, 5), 16)
  const b = parseInt(normalized.slice(5, 7), 16)
  return [r, g, b]
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
  return `#${[clamp(r), clamp(g), clamp(b)].map(v => v.toString(16).padStart(2, "0")).join("")}`.toUpperCase()
}

function parsePastedColor(value: string): string | null {
  const hex = normalizeHex(value)
  if (hex) return hex

  const rgbMatch = value.trim().match(/^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i)
  if (!rgbMatch) return null
  const [r, g, b] = rgbMatch.slice(1).map(Number)
  if ([r, g, b].some(n => !Number.isFinite(n) || n < 0 || n > 255)) return null
  return rgbToHex(r, g, b)
}

export const PaintbrushColorPicker: React.FC<PaintbrushColorPickerProps> = ({
  color,
  onChange,
  size = 16,
  title = "Change color",
  placement = "bottom"
}) => {
  const [open, setOpen] = React.useState(false)
  const [hexInput, setHexInput] = React.useState(color)
  const [rgbInput, setRgbInput] = React.useState<{ r: string; g: string; b: string }>({ r: "0", g: "0", b: "0" })
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const [popoverPos, setPopoverPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 })

  const popoverWidth = 420
  const popoverHeight = 250

  React.useEffect(() => {
    const normalized = normalizeHex(color) || "#000000"
    setHexInput(normalized)
    const [r, g, b] = hexToRgb(normalized)
    setRgbInput({ r: String(r), g: String(g), b: String(b) })
  }, [color])

  React.useEffect(() => {
    if (!open) return

    const updatePosition = () => {
      if (!buttonRef.current) return
      const rect = buttonRef.current.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight

      let left = rect.left
      let top = rect.bottom + 6

      if (placement === "right") {
        left = rect.right + 8
        top = rect.top + rect.height / 2 - popoverHeight / 2
      } else if (placement === "left") {
        left = rect.left - popoverWidth - 8
        top = rect.top + rect.height / 2 - popoverHeight / 2
      }

      const clampedLeft = Math.max(8, Math.min(left, vw - popoverWidth - 8))
      const clampedTop = Math.max(8, Math.min(top, vh - popoverHeight - 8))

      setPopoverPos({ top: clampedTop, left: clampedLeft })
    }

    updatePosition()

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickInsideTrigger = wrapperRef.current?.contains(target)
      const clickInsidePopover = popoverRef.current?.contains(target)
      if (!clickInsideTrigger && !clickInsidePopover) {
        setOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    const handleReposition = () => updatePosition()
    const handlePaste = (event: ClipboardEvent) => {
      const pasted = event.clipboardData?.getData("text") || ""
      const parsed = parsePastedColor(pasted)
      if (!parsed) return
      onChange(parsed)
      event.preventDefault()
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleKey)
    document.addEventListener("paste", handlePaste)
    window.addEventListener("resize", handleReposition)
    window.addEventListener("scroll", handleReposition, true)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleKey)
      document.removeEventListener("paste", handlePaste)
      window.removeEventListener("resize", handleReposition)
      window.removeEventListener("scroll", handleReposition, true)
    }
  }, [open, placement, onChange])

  const commitHex = (value: string) => {
    const normalized = normalizeHex(value)
    if (normalized) {
      onChange(normalized)
    } else {
      const fallback = normalizeHex(color) || "#000000"
      setHexInput(fallback)
    }
  }

  const commitRgb = () => {
    const parse = (value: string) => {
      const n = Number(value)
      if (!Number.isFinite(n)) return null
      return Math.max(0, Math.min(255, Math.round(n)))
    }
    const r = parse(rgbInput.r)
    const g = parse(rgbInput.g)
    const b = parse(rgbInput.b)
    if (r === null || g === null || b === null) {
      const [fr, fg, fb] = hexToRgb(color)
      setRgbInput({ r: String(fr), g: String(fg), b: String(fb) })
      return
    }
    const next = rgbToHex(r, g, b)
    onChange(next)
  }

  const copyHex = async () => {
    const normalized = normalizeHex(color) || "#000000"
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized)
      } else {
        const textarea = document.createElement("textarea")
        textarea.value = normalized
        textarea.style.position = "fixed"
        textarea.style.opacity = "0"
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        document.execCommand("copy")
        document.body.removeChild(textarea)
      }
    } catch {
      // ignore copy failures
    }
  }

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
      onClick={event => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        title={title}
        onClick={() => setOpen(prev => !prev)}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: "50%",
          backgroundColor: color,
          border: "1px solid rgba(0,0,0,0.2)",
          boxShadow: open ? "0 0 0 2px rgba(100, 113, 192, 0.45)" : "none",
          cursor: "pointer",
          padding: 0,
          transition: "box-shadow 0.15s ease"
        }}
      />
      {open && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          className="paintbrush-color-popover"
          style={{
            position: "fixed",
            top: popoverPos.top,
            left: popoverPos.left,
            zIndex: 9999,
            padding: "10px",
            background: "white",
            borderRadius: "6px",
            boxShadow: "0 6px 20px rgba(24, 36, 80, 0.18), 0 0 0 1px rgba(24, 36, 80, 0.06)"
          }}
        >
          <div className="d-flex" style={{ gap: "12px" }}>
            <div style={{ width: "196px" }}>
              <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                {DEFAULT_SWATCHES.map(swatch => (
                  <button
                    key={`default-${swatch}`}
                    type="button"
                    title={swatch}
                    onClick={() => {
                      onChange(swatch)
                    }}
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      border: "1px solid rgba(0,0,0,0.18)",
                      backgroundColor: swatch,
                      padding: 0
                    }}
                  />
                ))}
              </div>
            </div>
            <HexColorPicker
              color={color}
              onChange={onChange}
              style={{ width: "196px", height: "140px" }}
            />
          </div>
          <div className="d-flex align-items-center mt-2" style={{ gap: "8px", flexWrap: "nowrap" }}>
            <div className="d-flex align-items-center" style={{ gap: "6px", minWidth: "122px" }}>
              <span className="text-muted" style={{ fontSize: "12px" }}>#</span>
              <input
                type="text"
                className="form-control form-control-sm"
                value={hexInput.replace(/^#/, "")}
                maxLength={7}
                onChange={event => setHexInput(event.target.value)}
                onBlur={event => commitHex(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    commitHex((event.target as HTMLInputElement).value)
                  }
                }}
                spellCheck={false}
                style={{ fontFamily: "monospace", textTransform: "uppercase", width: "96px" }}
              />
            </div>
            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
              {(["r", "g", "b"] as const).map(channel => (
                <input
                  key={channel}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-control form-control-sm"
                  value={rgbInput[channel]}
                  onChange={event => setRgbInput(prev => ({ ...prev, [channel]: event.target.value }))}
                  onBlur={commitRgb}
                  onKeyDown={event => {
                    if (event.key === "Enter") commitRgb()
                  }}
                  aria-label={`RGB ${channel.toUpperCase()}`}
                  style={{ width: "44px", fontFamily: "monospace" }}
                />
              ))}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={copyHex}
            >
              Copy
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setOpen(false)
              }}
            >
              Select
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
